// Editor for a single policy rule (Phase 3).
//
// Every field is rendered from the server's ParamSpec — nothing about the 44
// rules is hardcoded here, so adding a rule to the catalog gives you a working
// form for free.
//
// The one thing worth reading twice is the supersede notice: editing a rule that
// is already in force does NOT overwrite it. The server closes it at yesterday
// and starts a successor today, so recalculating an earlier month still sees the
// policy that actually applied then. Users need to understand that, hence the
// inline explanation rather than a silent save.

import { useMemo, useState } from 'react';
import { X, Plus, Trash2, Info, CalendarClock, Users } from 'lucide-react';
import type {
  PolicyParamSpec,
  PolicyRuleRow,
  PolicyRuleSpec,
} from '../../api/policyRules';
import { choiceLabel, predicateLabel } from './policyLanguage';
import PolicyPredicateValue from './PolicyPredicateValue';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';

export interface EditorSubmit {
  params: Record<string, unknown>;
  applies_to: Record<string, unknown>;
  effective_from: string | null;
  effective_to: string | null;
  priority: number;
  notes: string | null;
}

interface Props {
  spec: PolicyRuleSpec;
  /** The row being edited; null = creating a new rule or exception. */
  row: PolicyRuleRow | null;
  /** Seed values when creating (usually the currently effective params). */
  seed?: Record<string, unknown> | null;
  /** Creating an exception exposes the window + predicate fields up front. */
  exception?: boolean;
  predicates: Record<string, string>;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (payload: EditorSubmit) => void;
  /** Turn this dialog into a new exception for the same rule. Receives the
   *  values currently on screen so the exception starts from what the rule
   *  says today. Omitted (or already in exception mode) hides the action. */
  onAddException?: (currentParams: Record<string, unknown>) => void;
}

const LIST_PREDICATES = new Set(['list[str]', 'list[int]']);

function parseList(raw: string, numeric: boolean): unknown[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (numeric ? Number(s) : s));
}

function listToText(value: unknown): string {
  return Array.isArray(value) ? value.join(', ') : '';
}

function ParamField({
  spec,
  value,
  onChange,
}: {
  spec: PolicyParamSpec;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const label = (
    <span className="block text-xs font-semibold text-gray-600">
      {spec.label || spec.name}
      {spec.help && (
        <span className="block font-normal text-[11px] text-gray-400 mt-0.5">{spec.help}</span>
      )}
    </span>
  );

  const input = 'border border-[#e8e3d8] rounded-lg px-2 py-1.5 text-sm w-full bg-white';

  if (spec.type === 'bool') {
    // Same shape as every other field — caption on top, control beneath — so
    // the two-column grid stays level. It used to be a bare <label> wrapping a
    // native checkbox, which broke on both counts: the app's theme renders any
    // <label> in serif italic (global-theme.css), so this one caption came out
    // italic while its neighbours were not, and the unstyled input showed as a
    // tiny hollow dot that nobody could tell was a switch.
    return (
      <div className="space-y-1">
        {label}
        <div className="flex items-center gap-2.5 h-9">
          <Switch
            checked={value === true}
            onCheckedChange={(next) => onChange(next)}
            aria-label={spec.label || spec.name}
          />
          <span className="text-sm text-gray-500">
            {value === true ? 'On' : 'Off'}
          </span>
        </div>
      </div>
    );
  }

  if (spec.type === 'enum') {
    // The app's own dropdown rather than a native <select>: the browser's
    // option list ignores the page styling entirely (blue OS highlight, system
    // font) and looked nothing like the rest of Teamlio.
    return (
      <div className="space-y-1">
        {label}
        <Select
          value={(value as string) ?? ''}
          onValueChange={(next) => onChange(next)}
        >
          <SelectTrigger aria-label={spec.label || spec.name} className="h-9">
            <SelectValue placeholder="Choose…" />
          </SelectTrigger>
          <SelectContent>
            {spec.choices.map((c) => (
              <SelectItem key={c} value={c}>{choiceLabel(c)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (spec.type === 'list[obj]') {
    const rows = Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
    const blank = Object.fromEntries(spec.item_fields.map((f) => [f.name, f.default]));
    return (
      <div className="space-y-1.5 sm:col-span-2">
        {label}
        {rows.map((item, i) => (
          <div key={i} className="flex items-end gap-2">
            {spec.item_fields.map((f) => (
              <label key={f.name} className="flex-1 min-w-0">
                <span className="block text-[10px] text-gray-400">{f.label || f.name}</span>
                <input
                  className={input}
                  type={f.type === 'int' || f.type === 'number' ? 'number' : 'text'}
                  value={(item[f.name] as string | number) ?? ''}
                  onChange={(e) => {
                    const next = [...rows];
                    const raw = e.target.value;
                    next[i] = {
                      ...item,
                      [f.name]:
                        f.type === 'int' || f.type === 'number'
                          ? (raw === '' ? null : Number(raw))
                          : raw,
                    };
                    onChange(next);
                  }}
                />
              </label>
            ))}
            <button
              type="button"
              aria-label="Remove entry"
              className="p-1.5 text-gray-400 hover:text-red-600"
              onClick={() => onChange(rows.filter((_, j) => j !== i))}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium"
          onClick={() => onChange([...rows, blank])}
        >
          <Plus size={13} /> Add entry
        </button>
      </div>
    );
  }

  if (spec.type === 'list[str]' || spec.type === 'list[int]') {
    return (
      <label className="block space-y-1">
        {label}
        <input
          className={input}
          value={listToText(value)}
          placeholder="comma separated"
          onChange={(e) => onChange(parseList(e.target.value, spec.type === 'list[int]'))}
        />
      </label>
    );
  }

  const htmlType =
    spec.type === 'int' || spec.type === 'number'
      ? 'number'
      : spec.type === 'time'
        ? 'time'
        : 'text';

  return (
    <label className="block space-y-1">
      {label}
      <input
        className={input}
        type={htmlType}
        placeholder={spec.type === 'month_day' ? 'MM-DD' : undefined}
        value={(value as string | number) ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') return onChange(null);
          onChange(htmlType === 'number' ? Number(raw) : raw);
        }}
      />
    </label>
  );
}

export default function PolicyRuleEditor({
  spec, row, seed, exception, predicates, saving, error, onCancel, onSubmit,
  onAddException,
}: Props) {
  const [params, setParams] = useState<Record<string, unknown>>(() => ({
    ...spec.defaults,
    ...(seed ?? {}),
    ...(row?.params ?? {}),
  }));
  const [applies, setApplies] = useState<Record<string, unknown>>(
    () => row?.applies_to ?? {},
  );
  const [from, setFrom] = useState(row?.effective_from ?? '');
  const [to, setTo] = useState(row?.effective_to ?? '');
  const [priority, setPriority] = useState(row?.priority ?? (exception ? 10 : 0));
  const [notes, setNotes] = useState(row?.notes ?? '');
  const [newPredicate, setNewPredicate] = useState('');

  // An in-force rule can't be edited in place — warn before the user saves.
  const willSupersede = useMemo(() => {
    if (!row) return false;
    if (!row.effective_from) return true;
    return row.effective_from <= new Date().toISOString().slice(0, 10);
  }, [row]);

  const showScope = Boolean(exception || row?.effective_from || row?.effective_to ||
    Object.keys(applies).length > 0);

  // Raising an exception only makes sense for a rule that can be narrowed by
  // dates or by people, and never while already writing one.
  const canAddException = Boolean(
    onAddException && !exception && (spec.windowed || spec.predicated),
  );

  const addPredicate = () => {
    if (!newPredicate) return;
    const type = predicates[newPredicate];
    setApplies((p) => ({ ...p, [newPredicate]: LIST_PREDICATES.has(type) ? [] : '' }));
    setNewPredicate('');
  };

  const submit = () => {
    onSubmit({
      params,
      applies_to: applies,
      effective_from: from || null,
      effective_to: to || null,
      priority: Number(priority) || 0,
      notes: notes.trim() || null,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label={`Edit ${spec.title}`}
    >
      <div className="bg-white rounded-2xl border border-[#e8e3d8] w-full max-w-2xl my-8">
        <div className="flex items-start justify-between p-4 border-b border-[#f0ece2]">
          <div className="min-w-0">
            <h2 className="font-semibold text-gray-900">
              {exception ? `Exception — ${spec.title}` : spec.title}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{spec.question}</p>
          </div>
          <button onClick={onCancel} aria-label="Close" className="p-1 text-gray-400 hover:text-gray-700">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4 max-h-[65vh] overflow-y-auto">
          {exception && (
            <div className="flex items-start gap-2 text-xs bg-violet-50 border border-violet-200 text-violet-900 rounded-lg p-2.5">
              <Users size={14} className="mt-0.5 shrink-0" />
              <p>
                This saves a <strong>separate rule</strong> that overrides
                {' '}{spec.title.toLowerCase()} for the dates and people you choose
                below. The main rule stays exactly as it is.
              </p>
            </div>
          )}

          {willSupersede && (
            <div className="flex items-start gap-2 text-xs bg-blue-50 border border-blue-200 text-blue-900 rounded-lg p-2.5">
              <Info size={14} className="mt-0.5 shrink-0" />
              <p>
                This rule is already in force. Saving keeps the current version for
                past dates and applies your change from <strong>today</strong>, so
                payroll for earlier months is unchanged.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {spec.params.map((p) => (
              <ParamField
                key={p.name}
                spec={p}
                value={params[p.name]}
                onChange={(v) => setParams((prev) => ({ ...prev, [p.name]: v }))}
              />
            ))}
          </div>

          {showScope && (
            <div className="space-y-3 border-t border-[#f0ece2] pt-3">
              {spec.windowed && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 flex items-center gap-1.5 mb-1.5">
                    <CalendarClock size={13} /> Only between these dates
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="block text-[10px] text-gray-400">From</span>
                      <input type="date" className="border border-[#e8e3d8] rounded-lg px-2 py-1.5 text-sm w-full"
                        value={from} onChange={(e) => setFrom(e.target.value)} />
                    </label>
                    <label className="block">
                      <span className="block text-[10px] text-gray-400">To</span>
                      <input type="date" className="border border-[#e8e3d8] rounded-lg px-2 py-1.5 text-sm w-full"
                        value={to} onChange={(e) => setTo(e.target.value)} />
                    </label>
                  </div>
                </div>
              )}

              {spec.predicated && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 flex items-center gap-1.5 mb-1.5">
                    <Users size={13} /> Only for people matching
                  </p>
                  <div className="space-y-2">
                    {Object.entries(applies).map(([key, value]) => (
                      <div key={key} className="flex items-start gap-2">
                        <span
                          title={key}
                          className="text-xs text-gray-600 w-40 shrink-0 pt-1.5"
                        >
                          {predicateLabel(key)}
                        </span>
                        <PolicyPredicateValue
                          predicateKey={key}
                          type={predicates[key]}
                          value={value}
                          onChange={(next) =>
                            setApplies((p) => ({ ...p, [key]: next }))
                          }
                        />
                        <button
                          type="button"
                          aria-label={`Remove ${key}`}
                          className="p-1 mt-1 text-gray-400 hover:text-red-600"
                          onClick={() => setApplies((p) => {
                            const next = { ...p };
                            delete next[key];
                            return next;
                          })}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <Select value={newPredicate} onValueChange={setNewPredicate}>
                        <SelectTrigger aria-label="Add condition" className="h-9 flex-1">
                          {/* Radix has no empty-valued item, so the "nothing
                              picked yet" state is the placeholder. */}
                          <SelectValue placeholder="Add a condition…" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.keys(predicates)
                            .filter((k) => !(k in applies))
                            .map((k) => (
                              <SelectItem key={k} value={k}>{predicateLabel(k)}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <button type="button" onClick={addPredicate}
                        className="text-xs text-emerald-700 font-medium inline-flex items-center gap-1">
                        <Plus size={13} /> Add
                      </button>
                    </div>
                  </div>
                  <label className="block mt-2">
                    <span className="block text-[10px] text-gray-400">
                      Priority — higher wins when several rules match the same person
                    </span>
                    <input type="number" className="border border-[#e8e3d8] rounded-lg px-2 py-1.5 text-sm w-32"
                      value={priority} onChange={(e) => setPriority(Number(e.target.value))} />
                  </label>
                </div>
              )}
            </div>
          )}

          <label className="block border-t border-[#f0ece2] pt-3">
            <span className="block text-xs font-semibold text-gray-600">
              Why this rule exists <span className="font-normal text-gray-400">(optional)</span>
            </span>
            <input
              className="border border-[#e8e3d8] rounded-lg px-2 py-1.5 text-sm w-full mt-1"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Per HR policy clause 3(b)"
            />
          </label>

          {error && (
            <p className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-lg p-2.5">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-[#f0ece2]">
          {/* Exceptions are raised from here — the rule being excepted is the
              one already open, so there is nothing to pick first. */}
          {canAddException ? (
            <button
              type="button"
              onClick={() => onAddException!(params)}
              disabled={saving}
              title="Apply a different value on certain dates, or to certain people"
              className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 border border-[#e8e3d8] rounded-lg px-2.5 py-1.5 hover:bg-[#faf8f3] hover:text-emerald-700 transition-colors disabled:opacity-50"
            >
              <Plus size={13} /> Add exception
            </button>
          ) : <span />}

          <div className="flex items-center gap-2">
            <button onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600">
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={saving}
              className="bg-emerald-700 text-white rounded-lg px-4 py-1.5 text-sm font-medium disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
